/**
 * The reporting period.
 *
 * WHAT A PERIOD CAN AND CANNOT TOUCH, and it is not a detail: the agent
 * extracts ledger and stock BALANCES with no date range at all (CLAUDE.md §8 —
 * a balance fetch with SVFROMDATE wedges TallyPrime). So closing balances are
 * all-time, full stop. Anything derived from them — Trial Balance, Balance
 * Sheet, Profit & Loss, Outstanding — cannot honestly be filtered by period,
 * and this module is deliberately not wired into them. Slicing those by date
 * would produce a number that looks period-specific and is not, which is worse
 * than not offering the control.
 *
 * What CAN be filtered is anything built from vouchers: the Day Book and the
 * Sales Register. Those are exactly the screens where "last month" is the
 * question people actually ask.
 */
export type PeriodId = "this-month" | "last-month" | "this-fy" | "last-fy" | "all" | "custom";

export type Period = {
  id: PeriodId;
  label: string;
  /** inclusive, YYYY-MM-DD; empty string means unbounded */
  from: string;
  to: string;
};

export const ALL_TIME: Period = { id: "all", label: "All time", from: "", to: "" };

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** Last calendar day of a month, leap years included. */
function endOfMonth(year: number, month: number): string {
  return iso(year, month, new Date(Date.UTC(year, month, 0)).getUTCDate());
}

/**
 * The Indian financial year runs April to March, so "this year" in an
 * accounting context is not the calendar year, and a January date belongs to
 * the FY that started the previous April.
 */
export function financialYearOf(date: string): { start: number } {
  const [year, month] = date.split("-").map(Number);
  return { start: (month ?? 1) >= 4 ? (year ?? 0) : (year ?? 0) - 1 };
}

export function presets(today: string): Period[] {
  const [y, m] = today.split("-").map(Number);
  const year = y ?? 1970;
  const month = m ?? 1;

  const prevMonth = month === 1 ? 12 : month - 1;
  const prevMonthYear = month === 1 ? year - 1 : year;
  const fy = financialYearOf(today).start;

  return [
    {
      id: "this-month",
      label: "This month",
      from: iso(year, month, 1),
      // Not "today": a month-to-date range that silently excludes an entry
      // dated later this month (post-dated invoices happen) reads as data loss.
      to: endOfMonth(year, month),
    },
    {
      id: "last-month",
      label: "Last month",
      from: iso(prevMonthYear, prevMonth, 1),
      to: endOfMonth(prevMonthYear, prevMonth),
    },
    {
      id: "this-fy",
      label: `FY ${fy}-${String(fy + 1).slice(2)}`,
      from: iso(fy, 4, 1),
      to: iso(fy + 1, 3, 31),
    },
    {
      id: "last-fy",
      label: `FY ${fy - 1}-${String(fy).slice(2)}`,
      from: iso(fy - 1, 4, 1),
      to: iso(fy, 3, 31),
    },
    ALL_TIME,
  ];
}

export function inPeriod(date: string, period: Period): boolean {
  if (period.from && date < period.from) return false;
  if (period.to && date > period.to) return false;
  return true;
}

/**
 * The same window a year earlier, for a year-on-year comparison.
 *
 * Shifting the YEAR rather than subtracting 365 days keeps month boundaries
 * intact — 365 days before 1 March lands on 2 March in a leap year, and a
 * comparison that quietly drops or doubles a day is not a comparison.
 */
export function lastYear(period: Period): Period {
  if (!period.from && !period.to) return period;

  const shift = (date: string): string => {
    if (!date) return date;
    const [y, m, d] = date.split("-");
    const year = Number(y) - 1;
    // 29 Feb has no counterpart in a common year; 28 Feb is the honest answer.
    if (m === "02" && d === "29" && !isLeap(year)) return `${year}-02-28`;
    return `${year}-${m}-${d}`;
  };

  return {
    ...period,
    id: "custom",
    label: `${period.label} last year`,
    from: shift(period.from),
    to: shift(period.to),
  };
}

function isLeap(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Percentage change, guarding the division that makes it useless. */
export function change(now: number, before: number): number | null {
  if (Math.abs(before) < 0.01) return null;
  return ((now - before) / Math.abs(before)) * 100;
}
